import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import logo from "../../assets/images/logo-georanking.png";
import type { IDashboardData } from "../dashboard/types";
import { fonts } from "../../styles/theme";
import { Imagem } from "./componentes/imagem";
import { lugarLimpo } from "./dados/formatar";
import {
  Apoio,
  Card,
  Esqueleto,
  Etiqueta,
  Grade,
  Largura,
  Meta,
  Pilula,
  Rodape,
  Secao,
  Topo,
  cores,
  revelar,
} from "./styles";

export const Cabecalho = ({
  voltar,
  acao,
  children,
}: {
  voltar?: { href: string; rotulo: string };
  acao?: { href: string; rotulo: string };
  children?: ReactNode;
}) => (
  <Largura>
    <Topo>
      <img src={logo} alt="GeoRanking" />
      <div className="acoes">
        {children}
        {voltar && <Link to={voltar.href}>{`← ${voltar.rotulo}`}</Link>}
        {acao && (
          <Pilula className="so-desktop" $tipo="contorno" href={acao.href} target="_blank" rel="noreferrer" style={{ padding: "11px 22px", fontSize: 14 }}>
            {acao.rotulo}
          </Pilula>
        )}
      </div>
    </Topo>
  </Largura>
);

export const Pe = ({ texto, escuro }: { texto: string; escuro?: boolean }) => (
  <Largura>
    <Rodape>
      <img src={logo} alt="GeoRanking" style={escuro ? { opacity: 0.8 } : undefined} />
      <Meta $claro={escuro}>{texto}</Meta>
    </Rodape>
  </Largura>
);

const Moldura = styled.div<{ $atraso?: number }>`
  ${({ $atraso }) => revelar($atraso ?? 0.1)}
  border: 1px solid ${cores.line};
  border-radius: 28px;
  overflow: hidden;
  background: #ffffff;

  @media (min-width: 900px) {
    border-radius: 40px;
  }
`;

const Foto = styled.div`
  height: 168px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #c9dbfb 0%, #ddebd9 55%, #f6e9c4 100%);

  .capa {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 55%, rgba(32, 33, 36, 0.28) 100%);
  }

  .miniaturas {
    position: absolute;
    right: 14px;
    bottom: 14px;
    display: flex;
    gap: 6px;
    z-index: 1;
  }

  .mini {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    border: 2px solid #ffffff;
    box-sizing: border-box;
    object-fit: cover;
    background: rgba(32, 33, 36, 0.72);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-size: 12px;
  }

  @media (min-width: 900px) {
    height: 280px;
    .miniaturas {
      right: 20px;
      bottom: 20px;
      gap: 8px;
    }
    .mini {
      width: 64px;
      height: 64px;
      border-radius: 16px;
      border-width: 3px;
      font-size: 14px;
    }
  }
`;

const Identidade = styled.div`
  padding: 0 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 3px;

  .avatar {
    width: 76px;
    height: 76px;
    border-radius: 999px;
    background: #ffffff;
    padding: 4px;
    margin-top: -42px;
    box-sizing: border-box;
    position: relative;
    flex-shrink: 0;
  }

  .avatar > div {
    width: 100%;
    height: 100%;
    border-radius: 999px;
    overflow: hidden;
    background: ${cores.blue};
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-size: 24px;
  }

  .avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .nome {
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-size: 22px;
    letter-spacing: -0.015em;
    padding-top: 8px;
    line-height: 1.2;
    overflow-wrap: anywhere;
  }

  .extras {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-top: 8px;
    flex-wrap: wrap;
  }

  .extras a {
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-size: 13px;
  }

  @media (min-width: 900px) {
    padding: 0 36px 28px;
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
    gap: 24px;

    .principal {
      display: flex;
      align-items: flex-end;
      gap: 20px;
    }
    .avatar {
      width: 112px;
      height: 112px;
      padding: 5px;
      margin-top: -56px;
    }
    .avatar > div {
      font-size: 34px;
    }
    .nome {
      font-size: 30px;
      padding-top: 0;
    }
    .extras {
      padding-bottom: 6px;
    }
    .extras a {
      font-size: 14px;
    }
  }
`;

const iniciais = (nome: string) =>
  nome
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte[0]?.toUpperCase() ?? "")
    .join("");

export const Capa = ({ perfil, etiqueta }: { perfil: IDashboardData; etiqueta?: ReactNode }) => {
  const fotos = perfil.photos ?? [];
  const avatar = fotos.find((foto) => foto.type === "PROFILE")?.photoUrl ?? null;
  const adicionais = fotos.filter((foto) => foto.type === "ADDITIONAL").map((foto) => foto.photoUrl);
  const miniaturas = adicionais.slice(1, 3);
  const restantes = Math.max(0, adicionais.length - 3);
  const linkDoPerfil = perfil.searchProfileUrl || perfil.profileUrl;

  return (
    <Largura>
      <Moldura>
        <Foto>
          <Imagem className="capa" candidatas={[...adicionais, avatar]} largura={1600} prioridade />
          {(miniaturas.length > 0 || restantes > 0) && (
            <div className="miniaturas">
              {miniaturas.map((url) => (
                <Imagem key={url} className="mini" candidatas={[url]} largura={200} />
              ))}
              {restantes > 0 && <div className="mini">{`+${restantes}`}</div>}
            </div>
          )}
        </Foto>
        <Identidade>
          <div className="principal">
            <div className="avatar">
              <div>
                <Imagem candidatas={[avatar]} largura={300} alt="" reserva={iniciais(perfil.profileName)} prioridade />
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <span className="nome">{perfil.profileName}</span>
              <Apoio style={{ margin: 0 }}>
                {[lugarLimpo(perfil.primaryCategory), lugarLimpo(perfil.cityState)].filter(Boolean).join(" · ")}
              </Apoio>
            </div>
          </div>
          <div className="extras">
            {etiqueta ?? (fotos.length > 0 && <Etiqueta>{`${fotos.length} ${fotos.length === 1 ? "foto" : "fotos"} no perfil`}</Etiqueta>)}
            {linkDoPerfil && (
              <a href={linkDoPerfil} target="_blank" rel="noreferrer">
                Ver perfil no Google
              </a>
            )}
          </div>
        </Identidade>
      </Moldura>
    </Largura>
  );
};

export const Carregando = () => (
  <Largura>
    <Secao aria-busy="true" aria-label="Carregando relatório">
      <Card style={{ display: "flex", flexDirection: "column", gap: 12, background: cores.grayTint }}>
        <Esqueleto $largura="60%" />
        <Grade>
          <Esqueleto $altura={64} />
          <Esqueleto $altura={64} />
        </Grade>
        <Esqueleto $altura={12} $largura="80%" />
      </Card>
    </Secao>
  </Largura>
);

const Centralizado = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;

  .titulo {
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-size: 16px;
    color: ${cores.blue};
  }
`;

export const EmPreparacao = () => (
  <Largura>
    <Secao>
      <Centralizado $fundo={cores.blueTint}>
        <img src={logo} alt="" style={{ height: 36 }} />
        <span className="titulo">Estamos preparando seu relatório</span>
        <Apoio style={{ margin: 0 }}>Assim que as primeiras medições ficarem prontas, ele aparece aqui.</Apoio>
      </Centralizado>
    </Secao>
  </Largura>
);

export const NaoEncontrado = () => (
  <Largura>
    <Secao>
      <Centralizado $fundo={cores.yellowTint}>
        <span className="titulo" style={{ color: cores.amber }}>
          Não encontramos este relatório
        </span>
        <Apoio style={{ margin: 0 }}>Confira o endereço recebido ou peça um novo link para a GeoRanking.</Apoio>
      </Centralizado>
    </Secao>
  </Largura>
);
